import styled, { css } from 'styled-components';

import { INTELLIGENCE_FONT_FAMILY } from 'containers/Intelligence/theme';
import {
  INTELLIGENCE_SCROLLBAR_SIZE,
  intelligenceScrollbar
} from 'containers/Intelligence/theme/scrollbar';
import { TYPE_LINE_HEIGHT, TYPE_SCALE, TypeStep } from 'containers/Intelligence/theme/tokens';

import { ORBIT_PANEL_WIDTH } from './constants';

/**
 * PLA-8844: Orbit's roles on the design system's type scale (2026-10-07). The panel mounts outside
 * the Intelligence scope, so it reads the scale's px directly rather than its custom properties;
 * each role is a size and its 140% line height, tracking 0.
 */
export const orbitRole = (step: TypeStep): string =>
  `font-size: ${TYPE_SCALE[step]}px; line-height: ${
    Math.round(TYPE_SCALE[step] * TYPE_LINE_HEIGHT * 100) / 100
  }px;`;

export const orbitType = {
  drawerTitle: orbitRole('body1'),
  drawerSubtitle: orbitRole('caption'),
  contextTitle: orbitRole('heading5'),
  contextSubtitle: orbitRole('body3'),
  chip: orbitRole('body3'),
  conversation: orbitRole('body1'),
  helping: orbitRole('caption'),
  containerTitle: orbitRole('body1'),
  containerBody: orbitRole('body3'),
  conflictTitle: orbitRole('body1'),
  conflictBody: orbitRole('body2'),
  conflictCompare: orbitRole('body3'),
  action: orbitRole('body1'),
  // Full-page Orbit only: the hero and page title sit above the shared type scale.
  pageHero: `font-size: 34px; line-height: ${Math.round(34 * TYPE_LINE_HEIGHT * 100) / 100}px;`,
  // The composer's fine print (6034:4876), below the shared type scale: 9 on div.fine's 13 box.
  disclaimer: 'font-size: 9px; line-height: 13px;',
  pageTitle: `font-size: 22px; line-height: ${Math.round(22 * TYPE_LINE_HEIGHT * 100) / 100}px;`
};

/**
 * Orbit panel colours as the Figma scene graph draws them (`figma-spec-v2.md` §5 colour tokens).
 * This is the DARK reading and the flag-OFF render: every value here is the literal the panel
 * shipped with, so a `perx_3_redesign` tenant is the only one that sees anything move.
 */
const ORBIT_COLORS_DARK = {
  surface: '#0D1428',
  surfaceAlt: '#101830',
  border: '#212D4B',
  text: '#F5F7FB',
  textSecondary: '#AEBBD0',
  // Lifted off the frame's #64709A, which is 3.77:1 on `surface` and 3.62:1 on `surfaceAlt`: this
  // token carries 9.5–12px body copy throughout the panel and so has to clear WCAG AA 4.5:1.
  // Same hue and saturation, +6% lightness — 4.76:1 and 4.57:1.
  textMuted: '#7681A6',
  accent: '#6D5DFC',
  action: '#268AFF',
  // The solid indigo the frame gives the primary buttons (`5032:10921`, `5032:9715`), one step
  // heavier than `action`, which inks links and outlines.
  actionStrong: '#4F46E5',
  onAction: '#FFFFFF',
  positive: '#1EB980',
  // TODO(AF-10): the colour is the design's, but no frame draws the over-limit composer state.
  negative: '#EE6475',
  bubbleUser: '#3E8FF0',
  // DELIBERATE DEVIATION, pending a designer answer. v3 moves this ink to #FFFFFF on the same
  // #3E8FF0 fill (5032:9121, 5032:8909), which takes it from 5.67:1 to 3.29:1. The bubble is
  // Inter Bold 12.5, well under WCAG's 18.66px large-text threshold, so the 4.5:1 floor applies
  // and the new value fails AA. Kept at the v2 value, as the muted-text lift above was.
  bubbleUserInk: '#04122C',
  threadText: '#EAF0F7',
  threadMuted: '#8DA0BD',
  threadFaint: '#5C6E8F',
  bubbleBorder: '#1A2440',
  surfaceDanger: '#0F1B2D',
  danger: '#EE6475',
  /*
    PERX3-REDESIGN round 8: the Access Changed card. The dark readings repeat the two literals the
    card already draws (`surfaceDanger` behind it, `danger` in the icon circle) and an inherited ink
    for the glyph, because the card's dark render is the drawn one and none of these four is read
    with the flag OFF - the `$perx3` branch in `OrbitAccessCard` is what reads them. They exist in
    this table because the light reading has to be a key of the same record.
  */
  accessCardBg: '#0F1B2D',
  accessCardBorder: 'transparent',
  accessIconBg: '#EE6475',
  accessIconInk: 'inherit',
  // antd's own error red, which the stand-in confirm modal inherits from the app rather than from
  // the panel's `danger`.
  modalError: '#ff4d4f',
  handoff: '#B18CF5',
  handoffFill: '#17362f',
  handoffInk: '#8fd4b9',
  successTick: '#59D9A0',
  // PERX3-REDESIGN round 7: the "Dashboard now shows" reading in the conflict card. It shared the
  // handoff tick's green, which read as a SUCCESS on a line that states a fact; under the flag it
  // is the body ink. Dark keeps the green it drew, verbatim.
  compareNewInk: '#59D9A0',
  warning: '#F3B95F',
  // `5032:9696` paints one amber at two opacities — a wash over the thread surface, not a ground.
  conflictFill: 'rgba(243, 185, 95, 0.14)',
  conflictStroke: 'rgba(243, 185, 95, 0.4)',
  surfaceConsole: '#05090F',
  placeholder: '#757575',
  disabledFill: '#B7B7B7',
  disabledInk: '#817F7F',
  // The donut legend's fourth designed series (`4952:8568`). It inks a decorative dot and a bar
  // fill, never text, so the panel's lifted `textMuted` is not the right value for it.
  seriesFourth: '#64709A',
  // Every DROP_SHADOW in the panel is the same black at 0.25 (`5017:9829`, `5032:7990`).
  shadow: 'rgba(0, 0, 0, 0.25)',
  mark: '#D2704C',
  // PLA-8844 (user, 2026-09-21): the assistant tile lost its gradient - it now wears the bubble's
  // own `surfaceAlt` + `bubbleBorder`, so only the mark's ink is still a role of its own, and the
  // drawn terracotta of `OrbitMarkReply` (`5199:10088`) replaces the white mark it carried.
  avatarAssistantMark: '#D2704C',
  launcherTile: '#D2704C',
  launcherBadge: '#4F46E5',
  launcherCard: '#FFFFFF',
  launcherCardInk: '#101830',
  // PERX3-REDESIGN. `chartActionInk` is the chart toolbar's glyph, which had been reading
  // `launcherCard` — the same #FFFFFF by coincidence, so under the light variant the toolbar went
  // white-on-white. A role of its own rather than `textSecondary`, whose dark reading is #AEBBD0 and
  // would have moved the flag-OFF toolbar. `launcherRing` is the hairline the avatar circle needs
  // only once its tile stops contrasting with the page; drawn as a box-shadow rather than a border
  // so it adds no layout, and `transparent` in the dark reading renders exactly what shipped.
  chartActionInk: '#FFFFFF',
  launcherRing: 'transparent',
  // PERX3-REDESIGN round 3. The header's own controls (close, new chat, save, history) had been
  // reading `launcherCard`, #FFFFFF by coincidence, so under the light variant the whole cluster
  // went white-on-white - the same defect `chartActionInk` fixed for the chart toolbar. The dark
  // readings are the literals the header already drew: #FFFFFF at rest and on hover (where the
  // feedback is the opacity fade, not an ink change), and `launcherTile`'s #D2704C while History
  // is pressed.
  headerControlInk: '#FFFFFF',
  headerControlHoverInk: '#FFFFFF',
  headerControlPressedInk: '#D2704C',
  // PERX3-REDESIGN round 4. The panel's solid primary button. The dark readings are the drawn
  // `actionStrong` #4F46E5 and the white on it, but no flag-OFF rule reads them: each consumer
  // keeps the `background`/`color` it shipped with and layers this trio only under `$perx3`,
  // because the three solid buttons disagree about their OFF ink (`onAction`, `threadText`).
  buttonBg: '#4F46E5',
  buttonInk: '#FFFFFF',
  buttonHoverBg: '#4F46E5'
} as const;

/**
 * PERX3-REDESIGN round 5: the eight stops of the redesign's single-hue tonal series ramp, dark to
 * light — the same values the Intelligence Suite declares, so a chart reads the same on either
 * surface.
 *
 * Declared in the LIGHT reading only, and read through a FALLBACK: `chartAdapters` asks for a stop
 * as `var(--orbit-series-2, var(--orbit-action))`, so with the flag off the custom property does not
 * exist and the hue that position already drew is what resolves. That is what keeps the flag-OFF
 * chart byte-identical without a second set of dark literals to drift from the first.
 */
const ORBIT_SERIES_LIGHT = {
  'series-1': '#202c46',
  'series-2': '#2665ee',
  'series-3': '#5b8af3',
  'series-4': '#8fb0f7',
  'series-5': '#bdd0fa',
  'series-6': '#dbe6fc',
  'series-7': '#4b5a7a',
  'series-8': '#8c8c8c'
} as const;

/**
 * PERX3-REDESIGN round 6: the answer card's vertical rhythm, declared in the LIGHT reading ONLY and
 * read through the same FALLBACK the series ramp uses — `padding: var(--orbit-space-card, 16px 18px
 * 24px)`. With the flag off the custom property does not exist, the fallback is the measured Figma
 * value, and the card renders exactly as it shipped; with the flag on the whole card steps onto one
 * 4px-based scale. No `$perx3` prop threads through `OrbitDataTable` or `OrbitChipRow`, which are
 * shared styled components with no props of their own and a dozen call sites between them.
 *
 * `label` (a section label to its body) is 6px in both readings and is declared so the rhythm is
 * readable in one place rather than being the one gap that lives only in a component.
 */
const ORBIT_SPACE_LIGHT = {
  'space-card': '16px',
  'space-section': '16px',
  'space-label': '6px',
  'space-row': '8px',
  'space-chip-top': '4px',
  'space-cta': '12px',
  'space-reason': '8px'
} as const;

export type OrbitThemeVariant = 'dark' | 'light';

/**
 * The Perx 3 reading of the same key set, derived from the dashboard's own palette (@primary-color
 * #2665ee, antd greys and softened status hues) so the panel matches the light Intelligence Suite
 * WU1 shipped. The `Record<keyof typeof ORBIT_COLORS_DARK, string>` annotation is the contract: a
 * key added above without a light reading fails the build rather than resolving to nothing.
 */
const ORBIT_COLORS_LIGHT: Record<keyof typeof ORBIT_COLORS_DARK, string> = {
  surface: '#ffffff',
  surfaceAlt: '#fafafa',
  border: '#f0f0f0',
  text: '#262626',
  textSecondary: '#595959',
  // antd grey-7 #8c8c8c reads 3.4:1 on #ffffff, under the 4.5:1 AA floor for text, and every one of
  // this token's ~14 consumers puts it on `color:`. grey-8 is 7.0:1 (6.7:1 on the #fafafa panels).
  textMuted: '#595959',
  accent: '#2665ee',
  action: '#2665ee',
  actionStrong: '#2665ee',
  onAction: '#ffffff',
  // green-7 #389e0d is 3.5:1 as text; green-8 #237804 is 5.6:1 on #ffffff and 5.4:1 on the #f6ffed
  // handoff fill. red-7 #cf1322 already clears AA at 5.6:1 and is left alone.
  positive: '#237804',
  negative: '#cf1322',
  // A soft tint of @primary-color rather than a saturated fill: the bubble carries 12.5px bold ink,
  // so it takes the body ink at 12.6:1 instead of white on blue at 3.3:1.
  bubbleUser: '#e8effd',
  bubbleUserInk: '#262626',
  threadText: '#262626',
  threadMuted: '#595959',
  threadFaint: '#595959',
  bubbleBorder: '#f0f0f0',
  surfaceDanger: '#fff1f0',
  danger: '#cf1322',
  // Round 8 (user): the light card was a pink wash behind a 3px red left rule and a dark-red circle,
  // which read as an error the reader had caused. It is a notice: the app's own neutral card ground
  // and border, and antd red-5 in the circle with white on it (3.8:1 - the glyph is a 20px
  // decorative mark with `aria-hidden`, and the card's own title and body carry the reading at
  // 15.9:1 and 7.0:1).
  accessCardBg: '#fafafa',
  accessCardBorder: '#f0f0f0',
  accessIconBg: '#ff4d4f',
  accessIconInk: '#ffffff',
  modalError: '#cf1322',
  handoff: '#7265c9',
  handoffFill: '#f6ffed',
  handoffInk: '#237804',
  successTick: '#237804',
  compareNewInk: '#262626',
  // `OrbitConflictCard`'s title is the only consumer and it is text: gold-7 #d48806 reads 2.9:1 and
  // even gold-8 #ad6800 only 4.4:1, so the ink takes gold-9 #874d00 at 6.8:1. The two rgba tints
  // below stay on the #d48806 channels - they are a fill and a stroke, not text.
  warning: '#874d00',
  conflictFill: 'rgba(212, 136, 6, 0.08)',
  conflictStroke: 'rgba(212, 136, 6, 0.3)',
  surfaceConsole: '#fafafa',
  placeholder: '#595959',
  disabledFill: '#f5f5f5',
  disabledInk: '#bfbfbf',
  seriesFourth: '#98a1b3',
  shadow: 'rgba(0, 0, 0, 0.08)',
  // Round 3 (user): the mark is drawn black, not blue, and its tiles are the neutral layout grey
  // rather than the blue tint that carried the blue mark.
  mark: '#262626',
  // Round 3 (user) keeps the light mark black; the terracotta above is the DARK reading only.
  avatarAssistantMark: '#262626',
  launcherTile: '#f0f2f5',
  // PERX3-REDESIGN round 6: the unread dot, and its only consumer is `OrbitLauncher`'s `StatusDot`.
  // At @primary-color it read as one more piece of brand chrome on a card whose tile is already
  // blue; antd red-5 is the app's own "there is something here" mark. The white ring the dot draws
  // around itself is `launcherCard` and is untouched.
  launcherBadge: '#ff4d4f',
  launcherCard: '#ffffff',
  launcherCardInk: '#262626',
  // grey-8, the body ink: the toolbar glyph is a control, and #ffffff on the #fafafa card it sits on
  // is 1.0:1. The ring is the app's own border grey, which is what gives the #e8effd avatar tile an
  // edge against the #f0f2f5 page it floats over.
  chartActionInk: '#595959',
  launcherRing: '#d9d9d9',
  // 7.0:1 at rest and 13.1:1 on hover against the #ffffff panel, both above the 4.5:1 a glyph this
  // small is held to. Pressed is @primary-color, the one state that has to read as a state rather
  // than as a darker glyph.
  headerControlInk: '#595959',
  headerControlHoverInk: '#262626',
  headerControlPressedInk: '#2665ee',
  // Round 4 (user): every solid primary button is black. #FFFFFF reads 15.9:1 on #262626 and 21:1
  // on the #000000 hover, so the 10.5px bold label the conflict card carries clears AA either way.
  buttonBg: '#262626',
  buttonInk: '#FFFFFF',
  buttonHoverBg: '#000000'
};

/**
 * Flag ON drops the Inter override so the panel inherits the app font, which is what
 * `font-family: inherit` resolves to once the variable is substituted.
 */
const ORBIT_FONT_FAMILY: Record<OrbitThemeVariant, string> = {
  dark: INTELLIGENCE_FONT_FAMILY,
  light: 'inherit'
};

type OrbitColorToken = keyof typeof ORBIT_COLORS_DARK;

const cssVarName = (token: string): string => `--orbit-${token}`;

const COLOR_TOKENS = Object.keys(ORBIT_COLORS_DARK) as OrbitColorToken[];

/**
 * The raw literals for a variant, for the two places a `var()` reference cannot go: an SVG `fill`
 * attribute on an exported mark, and the canvas background `html-to-image` paints behind a chart.
 */
export const orbitColorsFor = (variant: OrbitThemeVariant): Record<OrbitColorToken, string> =>
  variant === 'light' ? ORBIT_COLORS_LIGHT : ORBIT_COLORS_DARK;

/** The custom-property block `OrbitThemeScope` declares, one variant at a time. */
export const orbitTokenDeclarationsFor = (variant: OrbitThemeVariant): string => {
  const colors = orbitColorsFor(variant);

  return [
    ...COLOR_TOKENS.map(token => `${cssVarName(token)}: ${colors[token]};`),
    `${cssVarName('fontFamily')}: ${ORBIT_FONT_FAMILY[variant]};`,
    // Light only: see ORBIT_SERIES_LIGHT and ORBIT_SPACE_LIGHT. With the flag off none of these is
    // declared and every reference falls back to the value it already drew.
    ...(variant === 'light'
      ? Object.entries({ ...ORBIT_SERIES_LIGHT, ...ORBIT_SPACE_LIGHT }).map(
          ([token, value]) => `${cssVarName(token)}: ${value};`
        )
      : [])
  ].join('\n  ');
};

const colorVarRefs = {} as Record<OrbitColorToken, string>;

COLOR_TOKENS.forEach(token => {
  colorVarRefs[token] = `var(${cssVarName(token)})`;
});

/**
 * What the panel's styled-components read. Every colour is a reference into the block above, so the
 * variant is chosen once in `OrbitThemeScope` and the ~33 consumer files are untouched by it; the
 * two geometry values are shared by both readings and stay literal.
 *
 * Anything that mounts in a PORTAL (the two antd modals) escapes the scope's DOM subtree and
 * resolves every reference to nothing, so it has to carry its own `OrbitThemeScope`.
 */
export const orbitTokens = {
  panelWidth: ORBIT_PANEL_WIDTH,
  borderWidth: '0.8px',
  fontFamily: `var(${cssVarName('fontFamily')})`,
  ...colorVarRefs
} as const;

/**
 * One spacing token, falling back to the value the rule drew with the flag off - see
 * ORBIT_SPACE_LIGHT.
 */
export const orbitSpace = (token: keyof typeof ORBIT_SPACE_LIGHT, legacy: string): string =>
  `var(${cssVarName(token)}, ${legacy})`;

/**
 * One stop of the ramp above, falling back to the hue the position drew with the flag off. The
 * fallback is the whole mechanism - see ORBIT_SERIES_LIGHT.
 */
export const orbitSeriesStop = (stop: number, legacy: string): string =>
  `var(${cssVarName(`series-${((stop - 1) % 8) + 1}`)}, ${legacy})`;

/**
 * PERX3-REDESIGN round 4/7: the panel's one solid primary-button treatment under the flag — black
 * fill, white ink, no border, black on hover. Layered on a `$perx3` branch rather than folded into
 * the base rules for the reason the Intelligence twin is (`components/Intelligence/primaryButton`):
 * the three solid buttons ink `onAction` and `threadText` with the flag OFF, so one shared pair of
 * tokens cannot reproduce the OFF render, and leaving those declarations in place is what keeps it
 * byte-identical.
 */
export const perx3OrbitPrimaryButton = css`
  background: ${orbitTokens.buttonBg};
  color: ${orbitTokens.buttonInk};
  border: none;

  &:hover:not(:disabled):not([aria-disabled='true']) {
    background: ${orbitTokens.buttonHoverBg};
  }
`;

export const OrbitPanel = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${orbitTokens.panelWidth};
  max-width: 100%;
  height: 100%;
  background: ${orbitTokens.surface};
  /* 6034:4772: a 0.5 left edge and DROP_SHADOW 0,28 blur 60 #000000 alpha 0.45. */
  border-left: 0.5px solid ${orbitTokens.border};
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.45);
  color: ${orbitTokens.text};
  font-family: ${orbitTokens.fontFamily};

  &:focus {
    outline: none;
  }
`;

export const OrbitHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 16px 20px 16.8px;
  border-bottom: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
`;

export const OrbitHeaderGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 10px;
  }
`;

export const OrbitHeaderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 25px;
  /* figma-spec-v3.md section 2 re-reads node 5032:2064 as flat brand terracotta, not the two-stop
     gradient v2 recorded. 5.37:1 on the panel surface, above the 3:1 floor for a non-text vector. */
  color: ${orbitTokens.launcherTile};
  font-size: 20px;
`;

export const OrbitTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const OrbitTitle = styled.h2`
  margin: 0;
  color: ${orbitTokens.text};
  ${orbitType.drawerTitle}
  font-weight: 700;
`;

// 6034:5870: Inter Regular 10 on a 12 box.
export const OrbitSessionBadge = styled.span`
  color: ${orbitTokens.positive};
  font-size: 10px;
  line-height: 12px;
  text-transform: uppercase;
`;

/**
 * Right-hand controls of the header, one row centred on it (6034:5865, re-read 12:45): the chat
 * glyph at x 410-430, the chat-check at 439-456 and the close glyph at 466-480, about 9 apart.
 */
export const OrbitHeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md section 5). */
  > * + * {
    margin-left: 9px;
  }
`;

/**
 * Hover and focus label for a header glyph, drawn entirely in CSS from the box's own
 * `data-tooltip`.
 *
 * It replaces the antd `Tooltip` these four glyphs carried, because that Tooltip is the app's
 * heaviest native-ResizeObserver consumer. `rc-align` opens TWO observers per bubble - one on the
 * popup node, one on the trigger - through `resize-observer-polyfill`, which re-exports the NATIVE
 * implementation whenever `window.ResizeObserver` exists
 * (node_modules/resize-observer-polyfill/src/index.js:5-8), and it realigns from a microtask
 * inside that delivery loop while the bubble's own zoom transition is still changing its box.
 * Instrumented on the running dev server, one pass over the four glyphs opened 26 native observers
 * and re-measured each popup mid-transition (`browser-recheck22-20260922.md`); a pseudo-element
 * opens none. This also corrects `browser-recheck13-20260915.md`, which ruled the antd path out on
 * the belief that the polyfill never reaches the native API.
 *
 * The bubble hangs from the box's right edge so it grows INWARDS, which is what keeps it on screen
 * at the panel's flush-right edge (`browser-recheck12-20260914.md`), and `z-index: 4` clears the
 * three layers inside the panel it has to sit over: the sticky context block (1), the body scrim
 * (2) and the notice host (3). The accessible name is untouched - it stays on the button's
 * `aria-label` - and a pseudo-element is not in the accessibility tree, so nothing is announced
 * twice. The box is a flex one rather than a plain span because an inline one puts the button on
 * its text baseline and lifts the 17px glyphs 3px out of their row, and it is also what hears the
 * hover for the DISABLED Save glyph, which fires no events of its own in Safari
 * (conventions/components/cross-browser.md section 2). PLA-8844.
 */
export const OrbitHeaderTipBox = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  line-height: 0;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 4;
    width: max-content;
    max-width: 250px;
    padding: 6px 8px;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.75);
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
      0 9px 28px 8px rgba(0, 0, 0, 0.05);
    color: #ffffff;
    font-family: ${orbitTokens.fontFamily};
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    white-space: normal;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease-out;
  }

  &:hover::after,
  &:focus-within::after {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      transition: none;
    }
  }
`;

/**
 * A header action. The frame draws every glyph bare - no box, no fill, no radius - at its own
 * 12-20px size, so the button IS the glyph box and the hit area is grown off-box by 3px on each
 * side, under half of the 9px the frame leaves between the glyphs and so never overlapping a
 * neighbour.
 */
export const OrbitHeaderActionButton = styled.button<{ $w: number; $h: number }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.headerControlInk};
  cursor: pointer;
  font-size: 16px;
  line-height: 20px;

  &::after {
    content: '';
    position: absolute;
    inset: -3px;
  }

  /* The frame draws no hover state for these, so under the dark reading the feedback is the fade
     alone: headerControlHoverInk is the same #FFFFFF the rest ink already is. The light reading
     darkens the glyph as well, which is the only hover a grey-on-white cluster can show. */
  &:hover:not(:disabled) {
    opacity: 0.72;
    color: ${orbitTokens.headerControlHoverInk};
  }

  &:focus-visible {
    outline: 1px solid ${orbitTokens.action};
    outline-offset: 3px;
  }

  /* The only toggle in the header is History. The dark reading of headerControlPressedInk is the
     brand #D2704C the frames specify (5.37:1 on this surface, PLA-8844); it no longer borrows
     launcherTile, whose light reading is now the neutral tile behind the black mark and would have
     drawn the pressed glyph in #f0f2f5 on white. */
  &[aria-pressed='true']:not(:disabled) {
    color: ${orbitTokens.headerControlPressedInk};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// div.w-body 5032:10862 pads 18 / 20 / 24 / 20 and gaps its children by 4. A thread takes
// div#chat 6034:4796 instead: 20 / 16, its drawn 120 bottom being the composer it runs under. The
// scrollbar track is reserved right of the padding whether or not it shows, so the thread's right
// padding is the drawn 16 less the track: 499.5 - 16 - 8 - 8 leaves 467.5, and 445.5 beside the
// mark for the 445 reply column.
const THREAD_INSET = 16;

export const OrbitBody = styled.div<{ $thread?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Without this the scrolling axis computes the other one to auto, so any over-wide child adds a
     horizontal scrollbar to the whole panel instead of being contained. */
  overflow-x: hidden;
  padding: ${({ $thread }) =>
    $thread
      ? `20px ${THREAD_INSET - INTELLIGENCE_SCROLLBAR_SIZE}px 20px ${THREAD_INSET}px`
      : '18px 20px 24px'};

  /* The mixin's colours carry literal fallbacks precisely so the panel, which mounts outside every
     Intelligence route, still gets the themed bar rather than the OS one. */
  ${intelligenceScrollbar('y')}

  /* Safari < 14.1 has no flex gap, and this is a block box in any case. */
  > * + * {
    margin-top: 4px;
  }
`;

/**
 * Body + footer together, so an interrupting overlay can cover exactly what `Rectangle 728`
 * (`5032:7990`) covers: everything below the header, the composer included. The header stays
 * uncovered and interactive.
 */
export const OrbitBodyArea = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

// The layer the scrim covers. Its only job is to be one node the panel can mark inert.
export const OrbitBodyUnderlay = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

/**
 * `Rectangle 728` (`5032:7990` / `5032:9695`), the wash the conflict card floats on. The recorded
 * fill is opaque, so the transcript is hidden rather than dimmed; its recorded radius 10 is dropped
 * because a rounded cover over a square body would leave four corner slivers of the transcript.
 *
 * The 31px side padding is the mean of the card's drawn 35 left / 27 right inset, which puts the
 * card at its drawn 427 width inside the 489 panel. The child centres with `margin: auto` rather
 * than `align-items`, so a card taller than the panel scrolls instead of being clipped at the top.
 */
export const OrbitBodyScrim = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  padding: 24px 31px;
  overflow-y: auto;
  background: ${orbitTokens.surfaceAlt};
  /* 5032:7990 DROP_SHADOW 0,4 blur 4 #000000 alpha 0.25. */
  box-shadow: 0 4px 4px ${orbitTokens.shadow};

  ${intelligenceScrollbar('y')}

  > * {
    margin: auto 0;
  }
`;

// div.composer 6034:4869: a 1 top rule over 12 / 14 / 14 / 14.
export const OrbitFooter = styled.footer`
  flex: 0 0 auto;
  padding: 12px 14px 14px;
  border-top: 1px solid ${orbitTokens.border};
`;

export const OrbitBodyText = styled.p`
  margin: 0;
  color: ${orbitTokens.text};
  ${orbitType.containerBody}
`;

// TODO(AF-11): 10.5 / 0.525 is the majority across the newer panels (`4952:8992`); the idle panel
// still draws this label at 11 / 0.44. Awaiting the designer's call on which is canonical.
export const OrbitSectionLabel = styled.h3`
  margin: 0;
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
`;

// `4952:8096` (driver title) and `4952:8122` (Suggested Actions) letter-space the in-answer labels
// at 0.315, against the 0.525 the home and answer-detail labels use.
export const OrbitAnswerLabel = styled(OrbitSectionLabel)`
  letter-spacing: 0.315px;
`;

export const OrbitMutedText = styled.p`
  margin: 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.disclaimer}
  letter-spacing: 0.36px;
  text-align: center;
  text-transform: uppercase;
`;

export const OrbitTextButton = styled.button`
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.action};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 500;

  &:hover:not(:disabled),
  &:focus-visible {
    text-decoration: underline;
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const OrbitDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
`;

export const OrbitChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  /* PERX3-REDESIGN round 6: the chips sit 4px off the label under the flag; the 8px gap between
     them is OrbitChip's own margin and was already the redesign's value. */
  margin-top: ${orbitSpace('space-chip-top', '0px')};
  margin-bottom: -8px;
`;

/*
  5032:12026 is a 28px box with its 0.8 strokes inside it. Chrome snaps a 0.8px border up to a whole
  device pixel, so leaving the border outside the box measured 30.19 against a drawn 28: box-sizing
  plus the drawn height puts both borders back inside it.
*/
export const OrbitChip = styled.span`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 28px;
  max-width: 100%;
  margin: 0 8px 8px 0;
  padding: 5px 12.8px 4.2px;
  background: ${orbitTokens.surfaceAlt};
  border: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
  border-radius: 20px;
  color: ${orbitTokens.textSecondary};
  ${orbitType.chip}
  overflow-wrap: anywhere;

  /* The leading glyph, at the chip frame's own 6 itemSpacing (5032:12026). 12px rather than the
     10.5px text: it sits inside the 12.8px the 28px height leaves between the paddings, so the chip
     keeps its drawn geometry, and a line-art glyph reads a size below the letters it sits beside. */
  > .anticon {
    flex-shrink: 0;
    margin-right: 6px;
    font-size: 12px;
  }
`;

// Shared by the metric table and the per-metric series tables so the two read as one system.
export const OrbitDataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${orbitTokens.text};
  ${orbitType.containerBody}

  th,
  td {
    /* PERX3-REDESIGN round 6: 8px under the flag, the drawn 6 without it. */
    padding: ${orbitSpace('space-row', '6px')} 0;
    border-bottom: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
    text-align: left;
    vertical-align: top;
  }

  thead th {
    padding-top: 0;
    color: ${orbitTokens.textMuted};
    ${orbitType.helping}
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  /* Every column after the first is numeric and right-aligned, so its header follows it. */
  thead th + th {
    text-align: right;
  }

  tbody th {
    padding-right: 12px;
    font-weight: 400;
  }

  td {
    white-space: nowrap;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  td + td {
    padding-left: 12px;
  }
`;
